/**
 * QSignalBoard Component (v2.1)
 * Main container for Q Signal Board dashboard
 *
 * Layout:
 * - Header (44px): Q logo + title + symbol selector + view toggle + live indicator
 * - Content: Scrollable area for P&L + Signal cards (Grid or Table view)
 * - Footer (28px): Branding + timestamp
 *
 * v2.1 Updates (Phase 1B - Enhanced Signal Grid):
 * - Added symbol selector dropdown to header
 * - Symbol selection updates all indicator cards with full QuantX data
 *
 * v2.0 Updates (Phase 1 - MT5-style polish):
 * - Added optional view toggle between Grid and Table modes
 * - Grid mode uses QSignalGrid with QSignalGridCard components
 * - Maintains backward compatibility with existing QSignalTable
 *
 * @see docs/q-ai/Q_SignalBoard_Design.md
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Menu, MenuItem } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  COLORS,
  TYPOGRAPHY,
  SPACING,
  RADIUS,
  HEIGHTS,
  ANIMATIONS,
  SHARED_STYLES,
} from './signalBoardTheme';
import { pairToQuantX, quantXToDisplay } from '../../utils/symbolMapper';

/**
 * Default available symbols for Q.AI Coach (QuantX format)
 * These are fallback symbols when no signals are loaded
 *
 * AUDIT: Must match QTradeView Market Watch symbols exactly (8 total)
 */
const DEFAULT_QUANTX_SYMBOLS = [
  'XAUUSD_ct',   // Gold USD
  'XAUEUR_ct',   // Gold EUR
  'XAGUSD_ct',   // Silver USD
  'XAGEUR_ct',   // Silver EUR
  'EURUSD_ct',   // Euro/USD
  'GBPUSD_ct',   // Pound/USD
  'BTCUSD_ct',   // Bitcoin
  'ETHUSD_ct'    // Ethereum
];

/**
 * QSignalBoard - Main container component
 *
 * @param {React.ReactNode} children - Content to display (QSignalTable, QSignalGrid, etc.)
 * @param {boolean} isLive - Show live indicator
 * @param {string} lastUpdated - Last update timestamp
 * @param {boolean} showViewToggle - Show Grid/Table view toggle (default: false)
 * @param {string} viewMode - Current view mode: 'grid' | 'table'
 * @param {function} onViewModeChange - Callback when view mode changes
 * @param {string} selectedSymbol - Currently selected symbol for detailed view
 * @param {function} onSymbolChange - Callback when symbol changes
 * @param {array} availableSymbols - List of available symbols
 * @param {boolean} showSymbolSelector - Show symbol dropdown (default: false)
 * @param {object} priceData - Price context data from QuantX (Phase 1B)
 */
const QSignalBoard = ({
  children,
  isLive = false,
  lastUpdated = null,
  showViewToggle = false,
  viewMode = 'table',
  onViewModeChange,
  selectedSymbol = null,
  onSymbolChange,
  availableSymbols = DEFAULT_QUANTX_SYMBOLS,
  showSymbolSelector = false,
  priceData = null  // Phase 1B: { price, change, changePercent }
}) => {
  // Internal view state if no external control
  const [internalViewMode, setInternalViewMode] = useState(viewMode);
  const currentViewMode = onViewModeChange ? viewMode : internalViewMode;

  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null) {
      if (onViewModeChange) {
        onViewModeChange(event, newMode);  // Pass both event and newMode
      } else {
        setInternalViewMode(newMode);
      }
    }
  }, [onViewModeChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: COLORS.bgBoard,
        borderRadius: RADIUS.lg,
        border: `1px solid ${COLORS.borderDefault}`,
        overflow: 'hidden',
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════════
          HEADER - 44px
          ═══════════════════════════════════════════════════════════════════ */}
      <BoardHeader
        isLive={isLive}
        showViewToggle={showViewToggle}
        viewMode={currentViewMode}
        onViewModeChange={handleViewModeChange}
        selectedSymbol={selectedSymbol}
        onSymbolChange={onSymbolChange}
        availableSymbols={availableSymbols}
        showSymbolSelector={showSymbolSelector}
        priceData={priceData}
      />

      {/* ═══════════════════════════════════════════════════════════════════
          CONTENT AREA - Scrollable
          ═══════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          padding: SPACING.md,
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sm,
          ...SHARED_STYLES.scrollbar,
        }}
      >
        {children}
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER - 28px
          ═══════════════════════════════════════════════════════════════════ */}
      <BoardFooter lastUpdated={lastUpdated} />
    </Box>
  );
};

/**
 * Board Header - Q Logo + Title + Symbol Selector + Price Context + View Toggle + Live Indicator
 */
const BoardHeader = ({
  isLive,
  showViewToggle,
  viewMode,
  onViewModeChange,
  selectedSymbol,
  onSymbolChange,
  availableSymbols,
  showSymbolSelector,
  priceData
}) => {
  const [symbolMenuAnchor, setSymbolMenuAnchor] = useState(null);

  const handleSymbolClick = (event) => {
    if (onSymbolChange && availableSymbols?.length > 0) {
      setSymbolMenuAnchor(event.currentTarget);
    }
  };

  const handleSymbolSelect = (newSymbol) => {
    if (onSymbolChange) {
      onSymbolChange(newSymbol);
    }
    setSymbolMenuAnchor(null);
  };

  const handleSymbolClose = () => {
    setSymbolMenuAnchor(null);
  };

  return (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: HEIGHTS.header,
      padding: `0 ${SPACING.md}`,
      borderBottom: `1px solid ${COLORS.borderDefault}`,
      backgroundColor: COLORS.bgBoard,
      flexShrink: 0,
    }}
  >
    {/* Left: Q Icon + Title */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
      {/* Q Icon - 24x24 */}
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: RADIUS.sm,
          background: COLORS.brandGradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(39, 181, 109, 0.25)',
        }}
      >
        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: '0.75rem',
            fontWeight: 800,
            fontFamily: TYPOGRAPHY.title.fontFamily,
            lineHeight: 1,
          }}
        >
          Q
        </Typography>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          ...TYPOGRAPHY.title,
          color: COLORS.textPrimary,
          textTransform: 'none',
        }}
      >
        Q Analytics
      </Typography>

      {/* Symbol Selector (Enhanced Grid Feature) */}
      {showSymbolSelector && availableSymbols?.length > 0 && onSymbolChange && (
        <>
          <Box
            onClick={handleSymbolClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              padding: '4px 10px',
              borderRadius: RADIUS.sm,
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${COLORS.borderDefault}`,
              marginLeft: SPACING.sm,
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: COLORS.bgHover,
                borderColor: COLORS.borderHover
              }
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textPrimary,
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {selectedSymbol || 'SELECT'}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: COLORS.textMuted }} />
          </Box>

          {/* Symbol Dropdown Menu */}
          <Menu
            anchorEl={symbolMenuAnchor}
            open={Boolean(symbolMenuAnchor)}
            onClose={handleSymbolClose}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: COLORS.bgCard,
                  border: `1px solid ${COLORS.borderDefault}`,
                  minWidth: '140px',
                  mt: 0.5
                }
              }
            }}
          >
            {availableSymbols.map((sym) => (
              <MenuItem
                key={sym}
                onClick={() => handleSymbolSelect(sym)}
                selected={sym === selectedSymbol}
                sx={{
                  fontSize: '12px',
                  fontFamily: "'JetBrains Mono', monospace",
                  color: sym === selectedSymbol ? COLORS.signalBuy : COLORS.textPrimary,
                  padding: '8px 12px',
                  '&:hover': {
                    backgroundColor: COLORS.bgHover
                  },
                  '&.Mui-selected': {
                    backgroundColor: COLORS.signalBuyBg,
                    '&:hover': {
                      backgroundColor: 'rgba(39, 181, 109, 0.15)'
                    }
                  }
                }}
              >
                {sym}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Price Context (Phase 1B - Enhanced Signal Grid) */}
      {priceData?.price && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginLeft: SPACING.sm,
            padding: '3px 8px',
            borderRadius: RADIUS.sm,
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            border: `1px solid ${COLORS.borderDefault}`,
          }}
        >
          {/* Price */}
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: COLORS.textPrimary,
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {typeof priceData.price === 'number'
              ? priceData.price >= 1000
                ? priceData.price.toFixed(1)
                : priceData.price >= 1
                  ? priceData.price.toFixed(3)
                  : priceData.price.toFixed(5)
              : priceData.price}
          </Typography>

          {/* Change */}
          {priceData.changePercent !== undefined && (
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: priceData.changePercent >= 0 ? COLORS.signalBuy : COLORS.signalSell,
                fontFamily: "'JetBrains Mono', monospace"
              }}
            >
              {priceData.changePercent >= 0 ? '+' : ''}{priceData.changePercent?.toFixed(2)}%
            </Typography>
          )}
        </Box>
      )}
    </Box>

    {/* Right: View Toggle + Live Indicator */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.md }}>
      {/* View Toggle (Grid/Table) */}
      {showViewToggle && onViewModeChange && (
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newVal) => {
            console.log('[BoardHeader] ToggleButtonGroup onChange:', { newVal, eventType: e?.type });
            onViewModeChange(e, newVal);
          }}
          size="small"
          sx={{
            zIndex: 10,
            position: 'relative',
            '& .MuiToggleButton-root': {
              color: COLORS.textMuted,
              borderColor: COLORS.borderDefault,
              padding: '3px 8px',
              minWidth: '32px',
              pointerEvents: 'auto',
              '&:hover': {
                backgroundColor: COLORS.bgHover
              },
              '&.Mui-selected': {
                color: COLORS.signalBuy,
                backgroundColor: COLORS.signalBuyBg,
                borderColor: COLORS.signalBuyBorder,
                '&:hover': {
                  backgroundColor: 'rgba(39, 181, 109, 0.15)'
                }
              }
            }
          }}
        >
          <ToggleButton value="grid" aria-label="Grid View">
            <GridViewIcon sx={{ fontSize: '16px' }} />
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table View">
            <TableRowsIcon sx={{ fontSize: '16px' }} />
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      {/* Live Indicator */}
      {isLive && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          {/* Pulsing Dot */}
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: COLORS.signalBuy,
              ...ANIMATIONS.pulse,
            }}
          />
          {/* Live Text */}
          <Typography
            sx={{
              ...TYPOGRAPHY.caption,
              color: COLORS.signalBuy,
            }}
          >
            Live
          </Typography>
        </Box>
      )}
    </Box>
  </Box>
  );
};

/**
 * Board Footer - Branding + Timestamp (Enhanced visibility)
 * FONT FIX: Increased from 0.75rem (12px) to 0.875rem (14px) for better readability
 */
const BoardFooter = ({ lastUpdated }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: HEIGHTS.footer + 4, // Slightly taller for better visibility
      padding: `0 ${SPACING.md}`,
      borderTop: `1px solid rgba(255, 255, 255, 0.08)`,
      backgroundColor: COLORS.bgBoard,
      flexShrink: 0,
      gap: SPACING.sm,
    }}
  >
    <Typography
      sx={{
        fontSize: '0.875rem', // FONT FIX: 14px - institutional readable minimum
        fontWeight: 500,
        fontFamily: "'Inter', -apple-system, sans-serif",
        letterSpacing: '0.01em',
        color: COLORS.textSecondary, // #9ca3af - clear readable color
      }}
    >
      Q reads pre-computed data
    </Typography>

    {lastUpdated && (
      <>
        <Typography
          sx={{
            fontSize: '0.875rem', // FONT FIX: Match main text
            fontWeight: 400,
            color: COLORS.textMuted,
          }}
        >
          |
        </Typography>
        <Typography
          sx={{
            fontSize: '0.875rem', // FONT FIX: 14px - consistent with main text
            fontWeight: 500,
            fontFamily: "'Inter', -apple-system, sans-serif",
            letterSpacing: '0.01em',
            color: COLORS.textSecondary, // FONT FIX: Brighter color for readability
          }}
        >
          Updated: {lastUpdated}
        </Typography>
      </>
    )}
  </Box>
);

// PropTypes
QSignalBoard.propTypes = {
  children: PropTypes.node,
  isLive: PropTypes.bool,
  lastUpdated: PropTypes.string,
  showViewToggle: PropTypes.bool,
  viewMode: PropTypes.oneOf(['grid', 'table']),
  onViewModeChange: PropTypes.func,
  selectedSymbol: PropTypes.string,
  onSymbolChange: PropTypes.func,
  availableSymbols: PropTypes.arrayOf(PropTypes.string),
  showSymbolSelector: PropTypes.bool,
  priceData: PropTypes.shape({
    price: PropTypes.number,
    change: PropTypes.number,
    changePercent: PropTypes.number
  }),
};

BoardHeader.propTypes = {
  isLive: PropTypes.bool,
  showViewToggle: PropTypes.bool,
  viewMode: PropTypes.oneOf(['grid', 'table']),
  onViewModeChange: PropTypes.func,
  selectedSymbol: PropTypes.string,
  onSymbolChange: PropTypes.func,
  availableSymbols: PropTypes.arrayOf(PropTypes.string),
  showSymbolSelector: PropTypes.bool,
  priceData: PropTypes.shape({
    price: PropTypes.number,
    change: PropTypes.number,
    changePercent: PropTypes.number
  }),
};

BoardFooter.propTypes = {
  lastUpdated: PropTypes.string,
};

export default QSignalBoard;
